import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Avatar,
  Divider,
  Button,
  Chip,
  Stack,
} from '@mui/material';
import SpeedIcon from '@mui/icons-material/Speed';
import DescriptionIcon from '@mui/icons-material/Description';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {
  brand,
  pageHeroRichSxFor,
  pageElevatedCardSx,
  pageHeroImages,
  pageHeroType,
} from '../brandTheme';

/** Second visual for story column — office / advisory (distinct from hero) */
const ABOUT_STORY_IMAGE =
  'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=1600&q=80&auto=format&fit=crop';

const featureColors = [brand.solutionCard1, brand.solutionCard2, brand.solutionCard3, brand.indigo];

const features = [
  {
    title: 'Complete Financial Solutions',
    icon: <AccountBalanceWalletIcon fontSize="large" sx={{ color: '#fff' }} />,
  },
  {
    title: 'Instant Process',
    icon: <SpeedIcon fontSize="large" sx={{ color: '#fff' }} />,
  },
  {
    title: 'Minimum Documents',
    icon: <DescriptionIcon fontSize="large" sx={{ color: '#fff' }} />,
  },
  {
    title: '365 Days Service',
    icon: <SupportAgentIcon fontSize="large" sx={{ color: '#fff' }} />,
  },
];

const teamMembers = [
  {
    name: 'Anil Shah',
    role: 'Founder & CEO',
    image: '',
    bio: 'With over 15 years of experience in banking, Anil leads the vision of Om Sai Enterprises.',
  },
  {
    name: 'Akshay Shedge',
    role: 'Head of Operations',
    image: '',
    bio: 'Akshay Shedge ensures smooth processing and quick disbursals for all our customers.',
  },
  {
    name: 'Akshay Pathare',
    role: 'Senior Financial Advisor',
    image: '',
    bio: 'Akshay Pathare specializes in home loans and has helped over 500 families find their dream homes.',
  },
];

const milestones = [
  { year: '2010', title: 'Inception', description: 'Started as a small consultancy in Mumbai.' },
  { year: '2013', title: 'Expansion', description: 'Opened 3 new branches across the state.' },
  { year: '2016', title: '1000+ Customers', description: 'Reached a milestone of serving 1000 happy customers.' },
  { year: '2019', title: 'Digital Transformation', description: 'Launched online application portal for faster processing.' },
  { year: '2023', title: 'Award Winning', description: 'Recognized as "Best Financial Advisor" in the region.' },
];

const statsFromStory = [
  { value: '2010', label: 'Year founded', hint: milestones[0].description },
  { value: '3', label: 'Branches opened', hint: milestones[1].description },
  { value: '1000+', label: 'Customers served', hint: milestones[2].description },
  { value: '365', label: 'Days of service', hint: features[3].title },
];

function SectionHeading({ eyebrow, title, highlight, align = 'center' }) {
  return (
    <Box sx={{ textAlign: align, mb: { xs: 5, md: 6 } }}>
      {eyebrow && (
        <Typography
          sx={{
            color: brand.primary,
            fontWeight: 800,
            letterSpacing: '0.18em',
            fontSize: { xs: '0.8125rem', sm: '0.875rem' },
            display: 'block',
            mb: 2,
          }}
        >
          {eyebrow}
        </Typography>
      )}
      <Typography
        component="h2"
        sx={{
          fontWeight: 800,
          letterSpacing: '-0.025em',
          color: brand.slate,
          fontSize: { xs: '1.9rem', sm: '2.25rem', md: '2.65rem' },
          lineHeight: 1.15,
        }}
      >
        {title}{' '}
        {highlight && (
          <Box component="span" sx={{ color: brand.primary }}>
            {highlight}
          </Box>
        )}
      </Typography>
      <Box
        sx={{
          width: 72,
          height: 5,
          borderRadius: 2,
          background: `linear-gradient(90deg, ${brand.primary}, ${brand.indigo})`,
          mt: 2.5,
          mx: align === 'center' ? 'auto' : 0,
        }}
      />
    </Box>
  );
}

const About = () => {
  const heroSx = pageHeroRichSxFor(pageHeroImages.about);

  return (
    <Box className="component-box component-box--flush" sx={{ bgcolor: brand.surfaceWarm }}>
      {/* Hero */}
      <Box id="about-hero" sx={heroSx}>
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <Typography component="p" sx={{ ...pageHeroType.eyebrow, mb: 2.5 }}>
                WHO WE ARE
              </Typography>
              <Typography component="h1" sx={{ ...pageHeroType.title, mb: 2.5 }}>
                About <Box component="span" sx={{ color: '#fde68a' }}>Om Sai Enterprises</Box>
              </Typography>
              <Typography sx={{ ...pageHeroType.subtitle, mb: 4, maxWidth: 560 }}>
                Established with a vision to simplify financial access for every Indian.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button
                  variant="contained"
                  size="large"
                  href="#/contact"
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    px: 4,
                    py: 1.5,
                    borderRadius: '999px',
                    fontWeight: 800,
                    fontSize: '0.9375rem',
                    letterSpacing: '0.04em',
                    bgcolor: '#fbbf24',
                    color: '#422006',
                    boxShadow: '0 8px 28px rgba(0,0,0,0.25)',
                    '&:hover': { bgcolor: '#fcd34d' },
                  }}
                >
                  Get In Touch
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  href="#/services"
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  sx={{
                    borderRadius: '999px',
                    borderWidth: 2,
                    fontSize: '0.9375rem',
                    fontWeight: 700,
                    py: 1.5,
                    borderColor: 'rgba(255,255,255,0.55)',
                    color: '#fff',
                    '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.08)' },
                  }}
                >
                  View services
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12} md={5}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: '22px',
                  bgcolor: 'rgba(15,23,42,0.5)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  backdropFilter: 'blur(14px)',
                }}
              >
                <Grid container spacing={2}>
                  {statsFromStory.map((s) => (
                    <Grid item xs={6} key={s.label}>
                      <Box sx={{ textAlign: 'center', py: 2 }}>
                        <Typography sx={{ fontWeight: 800, fontSize: { xs: '2rem', sm: '2.25rem' }, color: '#fde68a', lineHeight: 1.1 }}>
                          {s.value}
                        </Typography>
                        <Typography sx={{ fontWeight: 700, fontSize: { xs: '0.9375rem', sm: '1rem' }, color: '#fff', mt: 1, lineHeight: 1.3 }}>
                          {s.label}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Credibility strip — ties stats to your milestone copy */}
      <Box sx={{ bgcolor: '#fff', borderBottom: `1px solid ${brand.border}`, py: { xs: 4, md: 4.5 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={3}>
            {statsFromStory.map((s) => (
              <Grid item xs={12} sm={6} md={3} key={s.label}>
                <Typography sx={{ color: brand.muted, lineHeight: 1.7, fontSize: { xs: '1rem', md: '1.0625rem' } }}>
                  <Box component="span" sx={{ fontWeight: 800, color: brand.slate, display: 'block', mb: 0.75, fontSize: '1.05rem' }}>
                    {s.value} · {s.label}
                  </Box>
                  {s.hint}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Story + image */}
      <Box sx={{ py: { xs: 8, md: 10 }, bgcolor: brand.surfaceWarm }}>
        <Container maxWidth="lg">
          <Grid container spacing={5} alignItems="center">
            <Grid item xs={12} md={6} sx={{ order: { xs: 2, md: 1 } }}>
              <SectionHeading eyebrow="OUR STORY" title="Built for clarity," highlight="trust, and speed" align="left" />
              <Typography sx={{ color: brand.muted, lineHeight: 1.85, mb: 2.5, fontSize: { xs: '1.0625rem', md: '1.125rem' } }}>
                {milestones[0].description} {milestones[1].description} {milestones[2].description}
              </Typography>
              <Typography sx={{ color: brand.muted, lineHeight: 1.85, fontSize: { xs: '1.0625rem', md: '1.125rem' } }}>
                {milestones[3].description} {milestones[4].description}
              </Typography>
              <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mt: 3.5 }}>
                <Chip
                  label={milestones[4].title}
                  sx={{ fontWeight: 700, fontSize: '0.9375rem', height: 36, bgcolor: brand.surface, border: `1px solid ${brand.border}` }}
                />
                <Chip
                  label={milestones[3].title}
                  sx={{ fontWeight: 700, fontSize: '0.9375rem', height: 36, bgcolor: brand.surface, border: `1px solid ${brand.border}` }}
                />
              </Stack>
            </Grid>
            <Grid item xs={12} md={6} sx={{ order: { xs: 1, md: 2 } }}>
              <Box
                component="img"
                src={ABOUT_STORY_IMAGE}
                alt=""
                sx={{
                  width: '100%',
                  borderRadius: '24px',
                  display: 'block',
                  boxShadow: '0 28px 60px rgba(15, 23, 42, 0.12)',
                  border: `1px solid ${brand.border}`,
                  minHeight: { xs: 280, md: 420 },
                  maxHeight: { md: 480 },
                  objectFit: 'cover',
                }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Why choose us */}
      <Box sx={{ py: { xs: 8, md: 10 }, bgcolor: '#fff', borderTop: `1px solid ${brand.border}` }}>
        <Container maxWidth="lg">
          <SectionHeading eyebrow="WHY US" title="Why choose" highlight="us?" />
          <Grid container spacing={3}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={3} key={feature.title}>
                <Paper
                  elevation={0}
                  sx={{
                    ...pageElevatedCardSx,
                    p: { xs: 3.25, md: 3.75 },
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                    '&:hover': { transform: 'translateY(-6px)', boxShadow: '0 28px 56px rgba(15, 23, 42, 0.12)' },
                  }}
                >
                  <Box
                    sx={{
                      mb: 2,
                      p: 1.75,
                      borderRadius: '14px',
                      backgroundColor: featureColors[index % featureColors.length],
                      display: 'inline-flex',
                    }}
                  >
                    {feature.icon}
                  </Box>
                  <Typography sx={{ fontWeight: 800, color: brand.slate, fontSize: { xs: '1.125rem', md: '1.2rem' }, lineHeight: 1.35 }}>
                    {feature.title}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Journey — vertical timeline */}
      <Box sx={{ py: { xs: 8, md: 10 }, bgcolor: brand.surfaceWarm }}>
        <Container maxWidth="md">
          <SectionHeading eyebrow="TIMELINE" title="Our" highlight="journey" />
          <Box sx={{ position: 'relative', pl: { xs: 3, sm: 4 }, mt: 2 }}>
            <Box
              sx={{
                position: 'absolute',
                left: { xs: 15, sm: 18 },
                top: 8,
                bottom: 8,
                width: 3,
                borderRadius: 2,
                background: `linear-gradient(180deg, ${brand.primary}, ${brand.indigo})`,
                opacity: 0.35,
              }}
            />
            <Stack spacing={3}>
              {milestones.map((m, idx) => (
                <Box key={m.year} sx={{ position: 'relative' }}>
                  <Box
                    sx={{
                      position: 'absolute',
                      left: { xs: -26, sm: -30 },
                      top: 20,
                      width: 14,
                      height: 14,
                      borderRadius: '50%',
                      bgcolor: idx === milestones.length - 1 ? brand.accent : brand.primary,
                      border: '3px solid #fff',
                      boxShadow: `0 0 0 2px ${brand.border}`,
                    }}
                  />
                  <Paper
                    elevation={0}
                    sx={{
                      ...pageElevatedCardSx,
                      p: { xs: 3.25, md: 3.75 },
                      ml: { xs: 1, sm: 2 },
                    }}
                  >
                    <Typography sx={{ color: brand.primary, fontWeight: 800, letterSpacing: '0.16em', fontSize: '0.875rem' }}>
                      {m.year}
                    </Typography>
                    <Typography sx={{ fontWeight: 800, color: brand.slate, my: 1, fontSize: { xs: '1.2rem', md: '1.35rem' } }}>
                      {m.title}
                    </Typography>
                    <Typography sx={{ color: brand.muted, lineHeight: 1.75, fontSize: { xs: '1.02rem', md: '1.0625rem' } }}>
                      {m.description}
                    </Typography>
                  </Paper>
                </Box>
              ))}
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* Team */}
      <Box sx={{ py: { xs: 8, md: 10 }, bgcolor: '#fff', borderTop: `1px solid ${brand.border}` }}>
        <Container maxWidth="lg">
          <SectionHeading eyebrow="LEADERSHIP" title="Meet the" highlight="team" />
          <Grid container spacing={3}>
            {teamMembers.map((member) => (
              <Grid item xs={12} md={4} key={member.name}>
                <Paper
                  elevation={0}
                  sx={{
                    ...pageElevatedCardSx,
                    p: 0,
                    overflow: 'hidden',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { sm: 'stretch' } }}>
                    <Box
                      sx={{
                        width: { xs: '100%', sm: 140 },
                        minHeight: { xs: 160, sm: 'auto' },
                        background: `linear-gradient(145deg, ${brand.primaryDark} 0%, ${brand.indigo} 100%)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        py: { xs: 3, sm: 0 },
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 88,
                          height: 88,
                          bgcolor: 'rgba(255,255,255,0.2)',
                          color: '#fff',
                          fontSize: '2rem',
                          fontWeight: 800,
                          border: '3px solid rgba(255,255,255,0.35)',
                        }}
                      >
                        {member.name[0]}
                      </Avatar>
                    </Box>
                    <Box sx={{ p: { xs: 3, md: 3.5 }, flex: 1, textAlign: { xs: 'center', sm: 'left' } }}>
                      <Typography sx={{ fontWeight: 800, color: brand.slate, fontSize: { xs: '1.25rem', md: '1.35rem' } }}>
                        {member.name}
                      </Typography>
                      <Chip
                        label={member.role}
                        sx={{
                          mt: 1.25,
                          mb: 2,
                          fontWeight: 700,
                          fontSize: '0.875rem',
                          height: 32,
                          bgcolor: brand.surface,
                          color: brand.primary,
                          border: `1px solid ${brand.border}`,
                        }}
                      />
                      <Divider sx={{ mb: 2, display: { xs: 'none', sm: 'block' } }} />
                      <Typography sx={{ color: brand.muted, lineHeight: 1.75, fontSize: { xs: '1.02rem', md: '1.0625rem' } }}>
                        {member.bio}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA */}
      <Box
        sx={{
          py: { xs: 6, md: 8 },
          background: `linear-gradient(115deg, ${brand.slate} 0%, #1e1b4b 50%, ${brand.primaryDark} 100%)`,
        }}
      >
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography sx={{ fontWeight: 800, color: '#fff', mb: 2.5, letterSpacing: '-0.02em', fontSize: { xs: '1.75rem', sm: '2rem', md: '2.25rem' } }}>
            Get in touch
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.9)', mb: 3.5, maxWidth: 520, mx: 'auto', lineHeight: 1.75, fontSize: { xs: '1.0625rem', md: '1.125rem' } }}>
            Established with a vision to simplify financial access for every Indian.
          </Typography>
          <Button
            variant="contained"
            href="#/contact"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            sx={{
              borderRadius: '999px',
              px: 4.5,
              py: 1.5,
              fontWeight: 800,
              fontSize: '0.9375rem',
              bgcolor: '#fbbf24',
              color: '#422006',
              '&:hover': { bgcolor: '#fcd34d' },
            }}
          >
            Contact Om Sai Enterprises
          </Button>
        </Container>
      </Box>
    </Box>
  );
};

export default About;
